<template>

    <div>

        <v-toolbar dark dense color="primary">
            <v-btn icon>
                <v-icon color="white">mdi-home-city-outline</v-icon>
            </v-btn>
            <v-toolbar-title>Locations</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-menu bottom
                    left>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn dark
                           icon
                           v-bind="attrs"
                           v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                </template>

                <v-list>
                    <v-list-item>
                        <v-list-item-title @click="menuLogOut">Logout</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title @click="exportReadsToCsv">Export Data</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title @click="confirmDataReset">Reset Data</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-toolbar>

        <v-container>
            <v-row>
                <v-col cols="auto" class="pa-2">
                    <v-text-field :value="buildingsSearchInput" label="Search"
                    clearable
                    style="min-width: 250px;"
                    @input="setBuildingsSearchInput"
                    ></v-text-field>
                </v-col>
                <v-col cols="auto" class="pa-2">
                    <v-switch
                    v-model="localShowAllBuildings"
                    :label="localShowAllBuildings ? 'All buildings' : 'Manually read buildings'"
                    ></v-switch>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" v-for="building in filteredBuildingsInOrder" :key="building.buildingId">
                    <v-card>
                        <v-card-title >{{ building.name }}</v-card-title>
                        <v-card-subtitle><v-icon class="px-1">{{building.productIcon}}</v-icon>{{ building.productType }}</v-card-subtitle>
                        <v-card-text>
                            <div class="text--primary">{{ building.address }}</div>
                            <p />

                            <div class="text--primary">
                                Completed &nbsp;
                                <v-progress-circular v-if="building.buildingId === submittedBuildingId" indeterminate size="20" />
                                <span v-else>{{ completedReads(building) }}/{{ building.totalMeters }}</span>
                            </div>
                            <v-progress-linear color="green darken-1"
                                               background-color="blue-grey lighten-4"
                                               rounded
                                               :value="getProgressPercentage(building)"
                                               height="18">
                            </v-progress-linear>
                            <v-progress-linear style="margin-top: -18px;"
                                               background-color="pink darken-4"
                                               rounded
                                               top
                                               :buffer-value="getUnsyncedPercentage(building)"
                                               height="18">
                            </v-progress-linear>

                        </v-card-text>

                        <v-card-actions style="justify-content: right;">
                            <v-btn v-if="getUnsyncedPercentage(building) > 0"
                                   color="primary"
                                   outlined
                                   large
                                   :loading="isSyncing && building.buildingId === submittedBuildingId"
                                   @click="handleSync(building)">
                                SYNC
                            </v-btn>
                            <v-btn color="green"
                                   outlined
                                   large
                                   :loading="isSubmitting && building.buildingId === submittedBuildingId"
                                   @click="handleSubmitReads(building)">
                                Submit
                            </v-btn>

                            <v-btn color="primary" class="text-white" depressed large
                                   @click="GoToBuildingMeters(building.buildingId)">
                                Enter Reads
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <v-snackbar v-model="snackbar"
                    :timeout="timeout"
                    :color="snackbarColor">
            {{ snackbarText }}

        </v-snackbar>
    </div>

</template>

<script>
    import { mapState, mapActions } from 'vuex'
    import exportFromJSON from 'export-from-json'
    import { alertConfirm } from '../utils/Alerts'
    import { deleteMmrDb } from '../providers/indexedDbService'

    export default {
        name: "BuildingsView",
        data() {
            return {
                building: null,
                snackbar: false,
                snackbarText: '',
                snackbarColor: 'primary',
                timeout: 3000,
                submittedBuildingId: null,
                isSyncing: false,
                isSubmitting: false, 
            }
        },
        async mounted() {
            await this.populateBuildingsFromCache()
        },
        computed: {
            ...mapState([
                'buildings',
                'buildingOrder',
                'reads',
                'showAllBuildings',
                'buildingsSearchInput'
            ]),
            name() {
                return this.$store.state.user.name
            },
            buildingsInOrder() {
                if (this.buildings.length === 0) return []
                return this.buildingOrder.map((id) => {
                    return this.buildings.filter(item => item.buildingId === id)[0]
                });
            },
            filteredBuildingsInOrder() {
                let filteredBuildingsInOrder = this.buildingsInOrder;
                if (this.buildingsSearchInput) {
                    filteredBuildingsInOrder = this.buildingsInOrder.filter(b =>  
                        b.name.toLowerCase().includes(this.buildingsSearchInput.toLowerCase()) || b.address.toLowerCase().includes(this.buildingsSearchInput.toLowerCase())         
                    );
                }
                if (!this.showAllBuildings) {
                    filteredBuildingsInOrder = filteredBuildingsInOrder.filter(b => b.manualReadRequired);
                }
                return filteredBuildingsInOrder;
            },
            unsyncedReads() {
                if (this.reads.length === 0) return []

                return this.reads.filter(r => r.meterRead && r.meterRead > 0 && r.status && r.status === 'PENDING')
            },
            localShowAllBuildings: {
                get() {
                    return this.showAllBuildings;
                },
                set(value) {
                    this.setShowAllBuildings(value);
                }
            }
        },
        methods: {
            ...mapActions([
                'populateBuildingsFromCache',
                'populateReadsFromCache',
                'submitBuildingReads',
                'LogOut', 
                'setShowAllBuildings',
                'setBuildingsSearchInput'
            ]),
            GoToBuildingMeters(id) {
                this.$store.commit('setActiveBuilding', id);
                return this.$router.push({
                    name: 'meters',
                })
            },
            async menuLogOut() {
                await this.LogOut();
                await this.$router.push("/login");
            },
            async handleSync(building) {
                if (!navigator.onLine) {
                    this.showSnackbar('Offline. Please connect to internet.', false);
                    return;
                }
                this.isSyncing = true
                this.submittedBuildingId = building.buildingId
                try {
                    this.$store.commit('setActiveBuilding', this.submittedBuildingId);
                    await this.resetBuildingCompleted(this.submittedBuildingId);
                }
                catch (err) {
                    this.showSnackbar('Error occurred during submit', false);
                }
                this.submittedBuildingId = null
                this.isSyncing = false
            },
            async handleSubmitReads(building) {
                if (!navigator.onLine) {
                    this.showSnackbar('Offline. Please connect to internet.', false);
                    return;
                }
                this.submittedBuildingId = building.buildingId
                this.isSubmitting = true
                try {
                    const completed = this.completedReads(building);
                    await this.submitBuildingReads(building.buildingId);
                    this.showSnackbar(`${completed} building reads submitted`, true);
                    await this.resetBuildingCompleted(building.buildingId);
                }
                catch (err) {
                    this.showSnackbar('Error occurred during submit', false);
                }
                this.submittedBuildingId = null
                this.isSubmitting = false
            },
            showSnackbar(message, isSuccess) {
                this.snackbarText = message
                this.snackbarColor = isSuccess ? 'green' : 'red';
                this.snackbar = true;
            },
            completedReads(building) {
                let completed = building.completedReads;
                if (this.reads.length > 0) {
                    completed = this.reads.filter((r) => r.buildingId === building.buildingId && r.meterRead > 0).length;
                }
                return completed;
            },
            async resetBuildingCompleted(buildingId) {
                await this.populateReadsFromCache(buildingId)
                await this.populateBuildingsFromCache()
            },
            async exportReadsToCsv() {
                await this.populateReadsFromCache(-1);
                const data = this.reads;
                const fileName = 'mmr_reads_export';
                const exportType = exportFromJSON.types.csv;

                exportFromJSON({ data, fileName, exportType });
            },
            getProgressPercentage(building) {
                return (this.completedReads(building) / building.totalMeters) * 100
            },
            getUnsyncedPercentage(building) {
                var unsynced = this.unsyncedReads.filter((r) => r.buildingId === building.buildingId).length
                return (unsynced / building.totalMeters) * 100
            },
            async confirmDataReset() {
                if (!navigator.onLine) {
                    this.showSnackbar('Offline. Please connect to internet.', false)
                    return
                }

                let response = await alertConfirm('Reset data, are you sure?', 'You are about to clear all data on your device and refresh from the server.')
                if (!response.isConfirmed) return

                if (this.unsyncedReads.length > 0) {
                    response = await alertConfirm('Unsynced reads!', 'You have unsynced reads, do you want to continue?')
                    if (!response.isConfirmed) return
                }
                await this.resetData()
            },
            async resetData() {
                await deleteMmrDb()
                this.showSnackbar('Data reset', true);
                location.reload(true)
            }
        }
    }
</script>

<style scoped>
</style>